import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { LuxAwardModalDispatchActions, LuxAwardModalDispatchContext, LuxAwardModalStateContext } from './LuxAwardModalContexts'
import TextButton from 'LuxKit/Button/TextButton'
import { PRIZES } from './luxAwardPrizes'
import Group from 'components/utils/Group'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'

interface Props {
  prizeKey: string
  exploreUrl: string
}

function LuxAwardModalStepPrize(props: Props) {
  const intl = useIntl()
  const { prizeKey } = props
  const prize = useMemo(() => PRIZES.find(prize => prize.key === prizeKey), [prizeKey])
  const state = useContext(LuxAwardModalStateContext)
  const dispatch = useContext(LuxAwardModalDispatchContext)
  const canGoPrev = useMemo(() => {
    return state.steps.indexOf(state.currentStep) > 1
  }, [state.currentStep, state.steps])

  const canGoNext = useMemo(() => {
    return state.steps.indexOf(state.currentStep) < state.steps.length - 1
  }, [state.currentStep, state.steps])

  const goHome = useCallback(() => {
    dispatch({ type: LuxAwardModalDispatchActions.GO_HOME })
  }, [dispatch])

  const goNext = useCallback(() => {
    dispatch({ type: LuxAwardModalDispatchActions.GO_NEXT })
  }, [dispatch])

  const goPrev = useCallback(() => {
    dispatch({ type: LuxAwardModalDispatchActions.GO_PREVIOUS })
  }, [dispatch])
  const backToPrizesText = intl.formatMessage({
    id: 'oWKTR6bGze1PZuLe9gLp',
    defaultMessage: 'Back to prizes',
    description: 'Subtitle link to navigate back to the prizes list in the Lux Award modal',
  })

  const exploreEscapesText = intl.formatMessage({
    id: 'N115ilvCX7R8BnjfBcfN',
    defaultMessage: 'Explore escapes',
    description: 'Primary action button text to explore available escapes in the Lux Award modal',
  })

  const viewPreviousText = intl.formatMessage({
    id: 'xt-ayhP14oIs6PK4W8ff',
    defaultMessage: 'View previous',
    description: 'Button text to navigate to the previous prize in the Lux Award modal',
  })

  const viewNextText = intl.formatMessage({
    id: 'BM_1ssw3z9MHZx7BAdm7',
    defaultMessage: 'View next',
    description: 'Button text to navigate to the next prize in the Lux Award modal',
  })

  if (!prize) {
    return null
  }
  return <>
    <ModalHeader
      title={prize.type}
      subtitle={backToPrizesText}
      onBackButtonClick={goHome}
    />
    <ModalBody>
      <ModalImageThumbnail image={prize.image} imageParams={{ aspectRatio: '3.15' }} />
      <ModalContent>
        <Group direction="vertical" gap={12}>
          <HeadingTextBlock variant="heading3">{prize.title}</HeadingTextBlock>
          <MarkdownRender fontSize="medium" type="compact" content={prize.description} />
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: exploreEscapesText,
        to: props.exploreUrl,
      }}
    >
      <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
        <TextButton kind="tertiary" onClick={goPrev} disabled={!canGoPrev} startIcon={<LineAngleLeftIcon />}>{viewPreviousText}</TextButton>
        <TextButton kind="tertiary" onClick={goNext} disabled={!canGoNext} endIcon={<LineAngleRightIcon />}>{viewNextText}</TextButton>
      </Group>
    </ModalFooter>
  </>
}

export default LuxAwardModalStepPrize
